import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import api from "@/lib/api";
import ProductTile from "@/components/store/ProductTile";
import { Scale, Eye, ExternalLink, Sparkles, ArrowUpRight } from "lucide-react";

/**
 * Public, read-only view of a compare snapshot. Anyone with the
 * link can view — no auth required. Matches the /store/compare
 * layout but strips the "remove column" and "add product" affordances
 * so agencies can drop this into a client email without worrying
 * about the client accidentally editing it.
 */

export default function CompareSnapshot() {
  const { sid } = useParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/store/compare/snapshots/${sid}`);
        setSnap(r.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "This snapshot has expired or the link is invalid."
            : "Couldn't load the shared comparison.",
        );
      }
    })();
  }, [sid]);

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/60">
          <div className="text-lg font-semibold text-red-300 mb-4">{error}</div>
          <Link to="/store" className="text-[#D4B878] underline">
            Browse the Mirrorloops Store →
          </Link>
        </div>
      </PublicShell>
    );
  }
  if (!snap) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/40">Loading…</div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div
        data-testid={`compare-snapshot-${snap.id}`}
        className="max-w-7xl mx-auto px-6 pt-32 pb-24"
      >
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-2 flex items-center gap-2">
            <Sparkles size={12} /> Shared comparison
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white">
            {snap.label || "Product shortlist"}
          </h1>
          {snap.note && (
            <p className="mt-3 text-white/60 max-w-2xl italic border-l-2 border-[#D4B878]/50 pl-3">
              {snap.note}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-white/40 font-mono">
            <span className="flex items-center gap-1">
              <Eye size={11} />
              {snap.view_count?.toLocaleString() || 0} views
            </span>
            <span>·</span>
            <span>{snap.products.length}-way comparison</span>
            {snap.created_by && (
              <>
                <span>·</span>
                <span>shared by {snap.created_by}</span>
              </>
            )}
          </div>
        </div>

        <div className="overflow-x-auto" data-testid="snapshot-compare-table">
          <div
            className="grid gap-3 min-w-[720px]"
            style={{
              gridTemplateColumns: `160px repeat(${snap.products.length}, minmax(200px, 1fr))`,
            }}
          >
            <div />
            {snap.products.map((p) => (
              <div
                key={p.id}
                data-testid={`snapshot-col-${p.id}`}
                className="border border-white/10 rounded-lg p-3 bg-white/[0.02]"
              >
                <div className="aspect-video mb-3">
                  <ProductTile p={p} size="md" />
                </div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#D4B878] font-semibold">
                  {p.manufacturer}
                </div>
                <Link
                  to={`/store/product/${p.id}`}
                  className="block text-sm font-medium text-white hover:text-[#E8D6A3] mt-1 leading-tight line-clamp-2"
                >
                  {p.name}
                </Link>
                <div className="text-[10px] text-white/40 mt-1 font-mono">{p.id}</div>
              </div>
            ))}

            {snap.matrix.map((row) => (
              <React.Fragment key={row.key}>
                <div
                  className={`px-2 py-2 text-[10px] uppercase tracking-[0.18em] flex items-center ${
                    row.differs ? "text-[#E8D6A3]" : "text-white/40"
                  }`}
                >
                  {row.label}
                </div>
                {row.values.map((v, i) => (
                  <div
                    key={`${row.key}-${i}`}
                    data-testid={`snapshot-cell-${row.key}-${i}`}
                    className={`px-3 py-2 text-xs rounded border ${
                      row.differs
                        ? "border-[#E8D6A3]/25 bg-[#E8D6A3]/[0.03] text-white"
                        : "border-white/10 bg-white/[0.01] text-white/70"
                    }`}
                  >
                    {v}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>

          <div
            className="mt-10 grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${snap.products.length}, minmax(240px, 1fr))`,
            }}
          >
            {snap.long_form.map((lf, i) => (
              <div
                key={lf.product_id}
                data-testid={`snapshot-longform-${lf.product_id}`}
                className="border border-white/10 rounded-lg p-4 bg-white/[0.02]"
              >
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] mb-2">
                  {snap.products[i]?.manufacturer}
                </div>
                {lf.recommendation_basis && (
                  <div className="text-xs text-white/70 leading-relaxed mb-3">
                    {lf.recommendation_basis}
                  </div>
                )}
                {lf.mirrorloops_use_case && (
                  <div className="p-2 border border-[#CCFF00]/20 rounded text-[11px] text-white/80 mb-3">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#CCFF00] mb-1">
                      Mirrorloops use case
                    </div>
                    {lf.mirrorloops_use_case}
                  </div>
                )}
                {lf.key_specs?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {lf.key_specs.map((k) => (
                      <span
                        key={k}
                        className="px-1.5 py-0.5 bg-white/[0.04] border border-white/10 rounded-full text-[9px] uppercase tracking-[0.14em] text-white/50 font-mono"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recreate CTA */}
        <div className="mt-14 border border-[#CCFF00]/25 rounded-lg p-6 bg-gradient-to-br from-[#CCFF00]/[0.03] to-transparent flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] mb-1">
              Build your own rack
            </div>
            <div className="text-white text-lg font-semibold leading-tight">
              Add these products to your own comparison
            </div>
            <div className="text-xs text-white/60 mt-1">
              Or start a fresh BOM from Mirrorloops's 4,128 SKU catalog.
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/store/compare?ids=${snap.product_ids.join(",")}`}
              data-testid="snapshot-recreate-compare"
              className="rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold px-4 py-2 text-sm flex items-center gap-1.5"
            >
              Open in Compare <ArrowUpRight size={12} />
            </Link>
            <Link
              to="/store/build"
              className="rounded-full border border-white/20 text-white hover:bg-white/5 px-4 py-2 text-sm flex items-center gap-1.5"
            >
              Start a BOM <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
